/** A package's composition (plan §3.5): its legs with their own catalogue rows (open them),
 *  the import mode and whether `package_ids` agrees, and the legs coverage verdict. The mode
 *  itself is edited as the "Package import" setting just above. */
import { Alert, Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { CataloguePackage, SettingSpec } from '@/api/catalogue';
import { formatDate } from '@/utils/format';

import { packageNotes, sourceLabel, STICKY_HINT, valueLabel } from './catalogueModel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function PackagePanel({
  pkg,
  kindSpec,
  grainSpec,
  onOpenProduct,
}: {
  pkg: CataloguePackage;
  kindSpec: SettingSpec | undefined;
  grainSpec: SettingSpec | undefined;
  onOpenProduct: (productId: number) => void;
}) {
  const mode = pkg.mode?.value as string | undefined;
  const notes = packageNotes(pkg.kind, mode);
  return (
    <Box sx={{ mt: 1.5 }} data-testid="package-panel">
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>{valueLabel(kindSpec, pkg.kind)}</strong> · imported{' '}
        <strong>{mode === 'whole' ? 'whole' : mode === 'legs' ? 'as legs' : (mode ?? '—')}</strong>
        {pkg.mode ? ` (${sourceLabel(pkg.mode.source)})` : ''} · package_ids{' '}
        {pkg.listed_in_package_ids ? 'lists it' : 'does not list it'}
      </Typography>
      {pkg.drift && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          The setting and <code>package_ids</code> disagree (edited outside the console?). Setting Package import again
          rewrites the membership.
        </Alert>
      )}
      {notes.map((n) => (
        <Alert key={n} severity="info" sx={{ mb: 1, py: 0 }}>
          {n}
        </Alert>
      ))}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {STICKY_HINT}
      </Typography>
      {pkg.coverage && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          {pkg.coverage.status === 'unknown'
            ? 'Legs are unknown until the package detail is fetched — refresh the listing.'
            : pkg.coverage.reason === 'no_legs'
              ? 'Its detail lists no legs: imported as legs, it imports nothing.'
              : `${pkg.coverage.legs.length} leg(s) would import nothing — select them and give them a tour.`}
        </Alert>
      )}
      {!pkg.legs_known ? (
        <Typography variant="body2" color="text.secondary">
          Legs are known after the package detail sweep (refresh the listing).
        </Typography>
      ) : (
        <Table size="small" aria-label="Package legs">
          <TableHead>
            <TableRow>
              <TableCell>Leg</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Selected</TableCell>
              <TableCell>Tours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pkg.legs.map((l) => (
              <TableRow key={`${l.product_id}-${l.option_id}`}>
                <TableCell>
                  {l.leg ? (
                    <Button
                      size="small"
                      onClick={() => onOpenProduct(l.leg!.id!)}
                      sx={{ p: 0, minWidth: 0, textTransform: 'none', textAlign: 'left' }}
                    >
                      {l.leg.name}
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      not in this catalogue
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary', display: 'block' }}>
                    {l.product_id}
                    {l.option_id ? ` / ${l.option_id}` : ''}
                  </Typography>
                  {l.leg?.removed_ts && (
                    <Chip size="small" label={`removed ${formatDate(l.leg.removed_ts)}`} color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </TableCell>
                <TableCell>{l.leg ? valueLabel(kindSpec, l.leg.kind) : '—'}</TableCell>
                <TableCell>{l.leg ? (l.leg.include ? 'yes' : 'no') : '—'}</TableCell>
                <TableCell>{l.leg ? valueLabel(grainSpec, l.leg.grain) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
